import { Box, Flex, Spinner, useDisclosure } from "@chakra-ui/react";
import { AccountPermission } from "@syla/shared/types/models/AccountAccessBase";
import React, { useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { EditAddressModal } from "../../components/molecules/forms/settingForms/EditAddressModal";
import { EditContactNumberModal } from "../../components/molecules/forms/settingForms/EditContactNumberModal";
import { AccountAbnEditable } from "../../components/organisms/accounts/AccountAbnEditable";
import { AccountEmailEditable } from "../../components/organisms/accounts/AccountEmailEditable";
import { hasAccountPermission } from "../../helper/permissions";
import { useCurrentAccountStore } from "../../store/currentAccountStore";
import { useQueryGetAccount } from "../../store/useQueryGetAccount";
import { Account } from "../../types/Account";
import { EditValueSettingRow } from "./components/EditValueSettingRow";
import { SettingRowBase } from "./components/SettingRowBase";
import {
  SectionHeader,
  SectionContainer,
  SettingsStack,
} from "./components/SettingsComponents";

export function AccountReportSettings() {
  const accountId = useCurrentAccountStore(({ accountId }) => accountId);

  const queryGetAccount = useQueryGetAccount(accountId);

  const formProps = useForm<Partial<Account>>({
    defaultValues: queryGetAccount.data,
  });

  const { reset } = formProps;

  useEffect(() => {
    reset(queryGetAccount.data);
  }, [queryGetAccount.data, reset]);

  const isLoading = queryGetAccount.isLoading || !queryGetAccount.data;

  return (
    <FormProvider {...formProps}>
      <SectionContainer>
        <SectionHeader
          title="Report settings"
          description="These optional details appear on the Crypto Tax Report."
        />
        {isLoading ?
          <Flex justifyContent="center" my="40px">
            <Spinner />
          </Flex>
        : <SettingsStack>
            <SettingRowBase
              label="Report email address"
              description="The email which appears on the Crypto Tax Report. Adjusting this will not affect the email you use to login to Syla."
            >
              <Box maxW="15em">
                <AccountEmailEditable account={queryGetAccount.data} />
              </Box>
            </SettingRowBase>
            <SettingRowBase
              label="ABN"
              description="The Australian Business Number (ABN) which appears on the Crypto Tax Report, if applicable."
            >
              <Box maxW="15em">
                <AccountAbnEditable account={queryGetAccount.data} />
              </Box>
            </SettingRowBase>
            <ContactNumberSettingRow account={queryGetAccount.data} />
            <AddressSettingRow account={queryGetAccount.data} />
          </SettingsStack>
        }
      </SectionContainer>
    </FormProvider>
  );
}

function ContactNumberSettingRow({ account }: { account: Account }) {
  const disclosure = useDisclosure();

  const canChangeAccountDetails = hasAccountPermission(
    account,
    AccountPermission.ChangeAccountDetails,
  );

  const contactNumberValue =
    account.contactNumber.number ?
      `${
        account.contactNumber.countryCode ?
          `${account.contactNumber.countryCode}`
        : ""
      } ${account.contactNumber.number}`
    : "";

  return (
    <>
      <EditValueSettingRow
        label="Contact number"
        description="The contact number which appears on the Crypto Tax Report."
        value={contactNumberValue}
        action={disclosure.onOpen}
        canEdit={canChangeAccountDetails}
      />
      {disclosure.isOpen && (
        <EditContactNumberModal
          currentCountryCode={account.contactNumber.countryCode || ""}
          currentNumber={account.contactNumber.number || ""}
          disclosure={disclosure}
        />
      )}
    </>
  );
}

function AddressSettingRow({ account }: { account: Account }) {
  const disclosure = useDisclosure();

  const canChangeAccountDetails = hasAccountPermission(
    account,
    AccountPermission.ChangeAccountDetails,
  );

  const addressParts = [
    account.address.suite,
    account.address.street,
    account.address.suburb,
    account.address.state,
    account.address.postcode,
  ].filter((value): value is string => Boolean(value?.trim()));

  const address = addressParts.join(", ");

  return (
    <>
      <EditValueSettingRow
        label="Address"
        description="The address which appears on the Crypto Tax Report."
        value={address}
        action={disclosure.onOpen}
        canEdit={canChangeAccountDetails}
      />
      {disclosure.isOpen && (
        <EditAddressModal
          currentAddress={account.address}
          disclosure={disclosure}
        />
      )}
    </>
  );
}
